import * as v2 from '@/core/services/api/v2';

/**
 * O mercado do dia, vindo do backend V2.
 *
 * `v2.mercado()` já devolve exatamente a forma que `UPDATE_MARKET` guarda —
 * a tradução de nomes (`horaDoServidor` → `serverTime`, ...) mora no adapter,
 * não aqui. `serverTime` entrou no estado porque a idade do peixe (depreciação
 * de preço) precisa do relógio do **servidor**: o relógio do jogador mente.
 */

const state = {
  lastUpdate: null,
  serverTime: null,
  favoriteFish: null,
  leastFavoriteFish: null,
  favoriteMap: null,
  leastFavoriteMap: null,
  dailyPriceMultipliers: {},
  eventGoldMultiplier: 1.0,
};

const mutations = {
  UPDATE_MARKET(state, marketData) {
    state.lastUpdate = marketData.lastUpdate;
    state.serverTime = marketData.serverTime || null;
    state.favoriteFish = marketData.favoriteFish;
    state.leastFavoriteFish = marketData.leastFavoriteFish;
    state.favoriteMap = marketData.favoriteMap;
    state.leastFavoriteMap = marketData.leastFavoriteMap;
    state.dailyPriceMultipliers = marketData.dailyPriceMultipliers;
  },
  SET_EVENT_GOLD_MULTIPLIER(state, multiplier) {
    state.eventGoldMultiplier = multiplier;
  }
};

const actions = {
  async updateMarketPrices({ commit, state }) {
    const today = new Date().toDateString();

    // O mercado só muda uma vez por dia; já buscado hoje, não busca de novo.
    if (state.lastUpdate === today) {
      return;
    }

    try {
      commit('UPDATE_MARKET', await v2.mercado());
    } catch (error) {
      console.error(
        'Falha ao buscar o mercado do dia:',
        error.response?.data?.message || error.response?.data?.error?.message || error.message,
      );
      // Erro transitório não deve calar o mercado o dia inteiro: o estado só
      // ganha os valores neutros se nunca carregou nada — e sem `lastUpdate`,
      // para que a próxima abertura da tela tente de novo.
      if (!state.lastUpdate) {
        commit('UPDATE_MARKET', {
          lastUpdate: null,
          serverTime: null,
          favoriteFish: null,
          leastFavoriteFish: null,
          favoriteMap: null,
          leastFavoriteMap: null,
          dailyPriceMultipliers: {}
        });
      }
    }
  },

  /**
   * O multiplicador de ouro dos eventos ativos.
   *
   * O V2 devolve a soma de **todos** os tipos de efeito numa resposta só
   * (zeros incluídos) — eram duas requisições no Strapi, uma por tipo.
   */
  async calculateEventGoldMultiplier({ commit }) {
    try {
      const efeitos = await v2.efeitosDeEvento();
      // 60% de "mais ouro" contra 10% de "menos ouro" vira 1.5.
      const multiplier = 1 + ((efeitos.moreGolds || 0) - (efeitos.lessGold || 0)) / 100;

      commit('SET_EVENT_GOLD_MULTIPLIER', multiplier);
      return multiplier;
    } catch (error) {
      console.error(
        'Falha ao somar efeitos de evento:',
        error.response?.data?.message || error.response?.data?.error?.message || error.message,
      );
      // Sem resposta, o neutro: preço base, nem bônus nem punição.
      commit('SET_EVENT_GOLD_MULTIPLIER', 1.0);
      return 1.0;
    }
  }
};

const getters = {
  getFishPriceMultiplier: (state) => (fish, rootState) => {
    if (!fish) return 1.0;

    let multiplier = 1.0;

    // Encontrar em qual mapa este peixe está disponível
    let fishMap = null;
    const allFishes = rootState.fishing.allFishes;

    // Percorre cada mapa no allFishes
    for (const [mapId, fishes] of Object.entries(allFishes)) {
      // Procura o peixe pelo nome neste mapa
      if (fishes.some(f => f.name === fish.name)) {
        fishMap = mapId;
        break;
      }
    }

    // Aplica multiplicador baseado no mapa
    if (fishMap) {
      if (fishMap === state.favoriteMap) {
        multiplier *= 1.5; // 50% bonus para peixes do mapa favorito
      } else if (fishMap === state.leastFavoriteMap) {
        multiplier *= 0.7; // 30% redução para peixes do mapa menos favorito
      }
    }

    // Aplica multiplicador baseado no peixe favorito/menos favorito
    if (state.favoriteFish && fish.name === state.favoriteFish.name) {
      multiplier *= 2.0;
    } else if (state.leastFavoriteFish && fish.name === state.leastFavoriteFish.name) {
      multiplier *= 0.3;
    } else if (state.dailyPriceMultipliers[fish.name]) {
      multiplier *= state.dailyPriceMultipliers[fish.name];
    }

    // Apply event gold multiplier
    multiplier *= state.eventGoldMultiplier;

    return multiplier;
  },
  getMarketInfo: (state) => ({
    favoriteFish: state.favoriteFish,
    leastFavoriteFish: state.leastFavoriteFish,
    favoriteMap: state.favoriteMap || 'pier', // Default to pier if undefined
    leastFavoriteMap: state.leastFavoriteMap || 'pier', // Default to pier if undefined
    dailyPriceMultipliers: state.dailyPriceMultipliers || {}
  })
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
